import { useState } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Download, Copy, Check, Search, Play, TrendingUp, Mail, CheckCircle2, Clock, Eye, ChevronDown, ChevronUp, Package, Truck, Ban } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

// ==================== Leads Tab ====================
export function LeadsTab({ leads }) {
  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{leads.length} lead{leads.length !== 1 ? "s" : ""}</p>
        <a href={`${API}/leads/export/csv`} download data-testid="btn-export-csv">
          <Button variant="ghost" className="text-sm" style={{ border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
        </a>
      </div>
      <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
        <div className="overflow-x-auto">
          <table className="admin-table" data-testid="leads-table">
            <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Type</th><th>Source</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td style={{ color: "var(--text-primary)", fontWeight: 500 }}>{lead.name}</td>
                  <td>{lead.email}</td>
                  <td>{lead.company || "---"}</td>
                  <td><span className="text-xs px-2 py-0.5" style={{ background: "rgba(15,50,33,0.06)", border: "1px solid rgba(15,50,33,0.15)", color: "var(--primary)" }}>{lead.inquiry_type || "general"}</span></td>
                  <td className="text-xs" style={{ color: "var(--text-tertiary)" }}>{lead.source}</td>
                  <td><span className={`status-badge ${lead.status}`}>{lead.status}</span></td>
                  <td className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>{formatDate(lead.created_at)}</td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={7} className="text-center py-8" style={{ color: "var(--text-tertiary)" }}>No leads yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ==================== Chat Sessions Tab ====================
export function ChatSessionsTab({ chatSessions }) {
  return (
    <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
      <div className="overflow-x-auto">
        <table className="admin-table" data-testid="chats-table">
          <thead><tr><th>Session</th><th>Visitor</th><th>Messages</th><th>Started</th><th>Last Active</th></tr></thead>
          <tbody>
            {chatSessions.map((s) => (
              <tr key={s.session_id}>
                <td className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>{s.session_id?.slice(0, 16)}...</td>
                <td>
                  <p style={{ color: "var(--text-primary)", fontWeight: 500, fontSize: "13px" }}>{s.visitor_name || "Anonymous"}</p>
                  <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>{s.visitor_email || "---"}</p>
                </td>
                <td style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-primary)" }}>{s.message_count}</td>
                <td className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>{formatDate(s.created_at)}</td>
                <td className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>{formatDate(s.updated_at)}</td>
              </tr>
            ))}
            {chatSessions.length === 0 && <tr><td colSpan={5} className="text-center py-8" style={{ color: "var(--text-tertiary)" }}>No chat sessions yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== Route Analyses Tab ====================
export function RouteAnalysesTab({ routeAnalyses }) {
  return (
    <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
      <div className="overflow-x-auto">
        <table className="admin-table" data-testid="routes-table">
          <thead><tr><th>Origin</th><th>Destination</th><th>Cargo</th><th>Routes</th><th>Date</th></tr></thead>
          <tbody>
            {routeAnalyses.map((r) => (
              <tr key={r.id}>
                <td style={{ color: "var(--text-primary)", fontWeight: 500 }}>{r.origin}</td>
                <td style={{ color: "var(--text-primary)" }}>{r.destination}</td>
                <td><span className="text-xs px-2 py-0.5" style={{ background: "rgba(15,50,33,0.06)", border: "1px solid rgba(15,50,33,0.15)", color: "var(--primary)" }}>{r.cargo_type}</span></td>
                <td style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-primary)" }}>{r.route_count || 0}</td>
                <td className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>{formatDate(r.created_at)}</td>
              </tr>
            ))}
            {routeAnalyses.length === 0 && <tr><td colSpan={5} className="text-center py-8" style={{ color: "var(--text-tertiary)" }}>No route analyses yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== Analytics Tab ====================
export function AnalyticsTab({ analyticsData, dailyStats, analyticsPeriod, setAnalyticsPeriod }) {
  if (!analyticsData) return null;
  return (
    <>
      <div className="flex gap-2 mb-6">
        {["day", "week", "month"].map((p) => (
          <button key={p} onClick={() => setAnalyticsPeriod(p)} className="px-4 py-1.5 text-xs uppercase tracking-wider font-semibold transition-colors" data-testid={`period-${p}`}
            style={{ background: analyticsPeriod === p ? "var(--accent)" : "var(--bg-secondary)", color: analyticsPeriod === p ? "#fff" : "var(--text-secondary)", border: "1px solid var(--border)" }}>
            {p}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="stat-card"><div className="stat-value">{analyticsData.totals.chats}</div><div className="stat-label">Total Chats</div></div>
        <div className="stat-card"><div className="stat-value">{analyticsData.totals.leads}</div><div className="stat-label">Total Leads</div></div>
        <div className="stat-card"><div className="stat-value">{analyticsData.totals.route_analyses}</div><div className="stat-label">Route Analyses</div></div>
        <div className="stat-card"><div className="stat-value">{analyticsData.conversion_rate}%</div><div className="stat-label">Conversion Rate</div></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="stat-card"><div className="stat-value" style={{ color: "var(--success)" }}>{analyticsData.period_counts.chat_sessions}</div><div className="stat-label">Chats ({analyticsPeriod})</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: "var(--success)" }}>{analyticsData.period_counts.leads}</div><div className="stat-label">Leads ({analyticsPeriod})</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: "var(--success)" }}>{analyticsData.period_counts.route_analyses}</div><div className="stat-label">Analyses ({analyticsPeriod})</div></div>
        <div className="stat-card"><div className="stat-value">{analyticsData.totals.submissions}</div><div className="stat-label">Submissions</div></div>
      </div>
      {dailyStats.length > 0 && (
        <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", padding: "24px" }}>
          <p className="text-xs uppercase tracking-wider mb-4" style={{ color: "var(--text-tertiary)" }}>Daily Activity (14 days)</p>
          <div className="flex items-end gap-1" style={{ height: "120px" }}>
            {dailyStats.map((d) => {
              const total = d.chats + d.leads + d.analyses;
              const maxH = Math.max(...dailyStats.map((x) => x.chats + x.leads + x.analyses), 1);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col justify-end" style={{ height: "90px" }}>
                    <div style={{ height: `${(total / maxH) * 90}px`, background: "var(--accent)", minHeight: total > 0 ? "4px" : "1px", opacity: total > 0 ? 1 : 0.2 }} />
                  </div>
                  <span className="text-[9px]" style={{ color: "var(--text-tertiary)" }}>{d.date.split(" ")[1]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {analyticsData.top_route_queries?.length > 0 && (
        <div className="mt-6" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", padding: "24px" }}>
          <p className="text-xs uppercase tracking-wider mb-4" style={{ color: "var(--text-tertiary)" }}>Top Route Queries</p>
          {analyticsData.top_route_queries.map((r) => (
            <div key={`${r.origin}-${r.destination}`} className="flex items-center justify-between py-2 text-sm" style={{ borderBottom: "1px solid var(--border)" }}>
              <span style={{ color: "var(--text-primary)" }}>{r.origin} &rarr; {r.destination}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>{r.count}x</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ==================== Marketing Intel Tab ====================
export function MarketingIntelTab({ marketingIntel, marketingScans, digestPreview, onRefetch }) {
  const [scanTriggering, setScanTriggering] = useState(false);
  const [digestSending, setDigestSending] = useState(false);

  const handleTriggerScan = async () => {
    setScanTriggering(true);
    try { await axios.post(`${API}/marketing/scan/trigger`); toast.success("Marketing scan triggered"); setTimeout(onRefetch, 8000); }
    catch { toast.error("Failed to trigger scan"); }
    finally { setScanTriggering(false); }
  };

  const handleSendDigest = async () => {
    setDigestSending(true);
    try { await axios.post(`${API}/marketing/digest/trigger`); toast.success("Weekly digest email sent"); }
    catch { toast.error("Failed to send digest"); }
    finally { setDigestSending(false); }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            {marketingIntel.length} intel item{marketingIntel.length !== 1 ? "s" : ""}
            {marketingScans.length > 0 && (
              <span className="ml-2" style={{ color: "var(--text-tertiary)" }}>
                --- Last scan: {marketingScans[0]?.status === "completed" ? "Completed" : marketingScans[0]?.status}
                {marketingScans[0]?.created_at && ` (${formatDate(marketingScans[0].created_at)})`}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleTriggerScan} disabled={scanTriggering} data-testid="btn-trigger-scan" className="text-sm" style={{ background: "var(--accent)", color: "#FFFFFF" }}>
            <Play size={14} className="mr-2" />{scanTriggering ? "Scanning..." : "Run Scan Now"}
          </Button>
          <Button onClick={handleSendDigest} disabled={digestSending} data-testid="btn-send-digest" variant="ghost" className="text-sm" style={{ border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
            <Mail size={14} className="mr-2" />{digestSending ? "Sending..." : "Send Digest"}
          </Button>
        </div>
      </div>

      {digestPreview && (
        <div className="mb-6" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", padding: "20px" }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-wider" style={{ color: "var(--accent)" }}>Weekly Digest Preview</p>
            <span className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>{digestPreview.period}</span>
          </div>
          <div className="grid grid-cols-4 gap-3 mb-3">
            {[
              { val: digestPreview.stats.new_leads, label: "New Leads" },
              { val: digestPreview.stats.chat_sessions, label: "Chats" },
              { val: digestPreview.stats.route_analyses, label: "Routes" },
              { val: digestPreview.stats.submissions, label: "Submissions" },
            ].map((s) => (
              <div key={s.label} className="text-center p-3" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)" }}>
                <div className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>{s.val}</div>
                <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>{s.label}</div>
              </div>
            ))}
          </div>
          <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>
            Recipient: <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{digestPreview.recipient}</span> --- Automatically sent every Monday 9am UTC
          </p>
        </div>
      )}

      {marketingScans.length > 0 && (
        <div className="mb-6" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", padding: "20px" }}>
          <p className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--text-tertiary)" }}>Recent Scans</p>
          <div className="flex gap-4 overflow-x-auto">
            {marketingScans.map((scan) => (
              <div key={scan.scan_id} className="flex-shrink-0 text-xs p-3" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)", minWidth: "180px" }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${scan.status === "completed" ? "bg-green-500" : "bg-red-500"}`} />
                  <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{scan.status}</span>
                </div>
                <p style={{ color: "var(--text-tertiary)" }}>{scan.items_generated} items | {scan.keywords_analyzed?.length || 0} keywords</p>
                <p style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)", fontSize: "10px" }}>{formatDate(scan.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4" data-testid="marketing-intel-list">
        {marketingIntel.map((item) => (
          <div key={item.id} style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", padding: "24px" }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{item.keyword}</h3>
                <p className="text-xs mt-1" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>{formatDate(item.created_at)}</p>
              </div>
              <div className="flex items-center gap-1 px-3 py-1" style={{ background: item.trending_score >= 7 ? "rgba(22,163,74,0.08)" : "rgba(15,50,33,0.04)", border: `1px solid ${item.trending_score >= 7 ? "rgba(22,163,74,0.2)" : "var(--border)"}` }}>
                <TrendingUp size={12} style={{ color: item.trending_score >= 7 ? "#16A34A" : "var(--text-tertiary)" }} />
                <span className="text-xs font-semibold" style={{ color: item.trending_score >= 7 ? "#16A34A" : "var(--text-secondary)" }}>{item.trending_score}/10</span>
              </div>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>{item.summary}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--accent)" }}>Opportunities</p>
                <ul className="space-y-1">{(item.opportunities || []).map((opp) => (<li key={opp} className="text-xs" style={{ color: "var(--text-secondary)", lineHeight: 1.5 }}>- {opp}</li>))}</ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--accent)" }}>Forum Themes</p>
                <ul className="space-y-1">{(item.forum_themes || []).map((theme) => (<li key={theme} className="text-xs" style={{ color: "var(--text-secondary)", lineHeight: 1.5 }}>- {theme}</li>))}</ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--accent)" }}>Content Ideas</p>
                <ul className="space-y-1">{(item.content_ideas || []).map((idea) => (<li key={idea} className="text-xs" style={{ color: "var(--text-secondary)", lineHeight: 1.5 }}>- {idea}</li>))}</ul>
              </div>
            </div>
            {item.suggested_meta_tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {item.suggested_meta_tags.map((tag) => (<span key={tag} className="text-xs px-2 py-1" style={{ background: "rgba(15,50,33,0.06)", border: "1px solid rgba(15,50,33,0.12)", color: "var(--primary)" }}>{tag}</span>))}
              </div>
            )}
          </div>
        ))}
        {marketingIntel.length === 0 && (
          <div className="text-center py-16" style={{ color: "var(--text-tertiary)" }}>
            <Search size={32} className="mx-auto mb-4 opacity-30" />
            <p className="text-sm mb-2">No marketing intelligence yet</p>
            <p className="text-xs">Click &ldquo;Run Scan Now&rdquo; to generate your first marketing report</p>
          </div>
        )}
      </div>
    </>
  );
}

// ==================== Orders Tab ====================
const ORDER_STATUSES = ["pending_payment", "paid", "shipped", "delivered", "cancelled"];
const ORDER_STATUS_STYLE = {
  pending_payment: { color: "#D97706", icon: Clock, label: "Pending Payment" },
  paid: { color: "#16A34A", icon: CheckCircle2, label: "Paid" },
  shipped: { color: "#2563EB", icon: Truck, label: "Shipped" },
  delivered: { color: "#0F3221", icon: Package, label: "Delivered" },
  cancelled: { color: "#DC2626", icon: Ban, label: "Cancelled" },
};

export function OrdersTab({ orders, onRefetch }) {
  const axiosAdmin = axios.create({ withCredentials: true });
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [filter, setFilter] = useState("all");

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axiosAdmin.patch(`${API}/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Order updated to ${newStatus}`);
      onRefetch();
    } catch { toast.error("Failed to update"); }
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);
  const pendingCount = orders.filter((o) => o.status === "pending_payment").length;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            {pendingCount > 0 && <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold mr-2" style={{ background: "var(--warning)", color: "#fff", borderRadius: "50%" }}>{pendingCount}</span>}
            {orders.length} order{orders.length !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-1 flex-wrap">
            {["all", ...ORDER_STATUSES].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-order-${f}`}
                className="px-3 py-1 text-xs uppercase tracking-wider font-semibold transition-colors"
                style={{ background: filter === f ? "var(--accent)" : "var(--bg-secondary)", color: filter === f ? "#fff" : "var(--text-secondary)", border: "1px solid var(--border)" }}>
                {f === "all" ? "All" : ORDER_STATUS_STYLE[f]?.label || f}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3" data-testid="orders-list">
        {filtered.map((order) => {
          const statusInfo = ORDER_STATUS_STYLE[order.status] || ORDER_STATUS_STYLE.pending_payment;
          const StatusIcon = statusInfo.icon;
          return (
            <div key={order.id} style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
              <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)} data-testid={`order-row-${order.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <StatusIcon size={16} style={{ color: statusInfo.color }} className="flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-primary)" }}>#{order.id}</span>
                      <span className="text-sm" style={{ color: "var(--text-primary)" }}>{order.name}</span>
                    </div>
                    <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>
                      {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? "s" : ""} &middot; {formatDate(order.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-sm font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-primary)" }}>${order.total?.toFixed(2)}</span>
                  <span className="text-xs px-2 py-0.5 font-semibold" style={{ background: `${statusInfo.color}10`, color: statusInfo.color, border: `1px solid ${statusInfo.color}30` }}>{statusInfo.label}</span>
                  {expandedOrder === order.id ? <ChevronUp size={14} style={{ color: "var(--text-tertiary)" }} /> : <ChevronDown size={14} style={{ color: "var(--text-tertiary)" }} />}
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="px-4 pb-4" style={{ borderTop: "1px solid var(--border)" }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--text-tertiary)" }}>Contact</p>
                      <p className="text-sm" style={{ color: "var(--text-primary)" }}>{order.name}</p>
                      <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{order.email}</p>
                      <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{order.phone}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--text-tertiary)" }}>Ship To</p>
                      <p className="text-sm" style={{ color: "var(--text-primary)" }}>{order.shipping?.line1}</p>
                      {order.shipping?.line2 && <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{order.shipping.line2}</p>}
                      <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{order.shipping?.city}, {order.shipping?.state} {order.shipping?.zip}</p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--text-tertiary)" }}>Items</p>
                    {order.items?.map((item, i) => (
                      <div key={i} className="flex justify-between py-1.5 text-sm" style={{ borderBottom: "1px solid var(--border)" }}>
                        <span style={{ color: "var(--text-secondary)" }}>{item.product} x{item.quantity}</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-primary)" }}>${item.subtotal?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  {order.special_requests && (
                    <div className="mt-3 p-3" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)" }}>
                      <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: "var(--text-tertiary)" }}>Special Requests</p>
                      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{order.special_requests}</p>
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-2">
                    <p className="text-xs uppercase tracking-wider font-bold mr-2" style={{ color: "var(--text-tertiary)" }}>Update Status:</p>
                    {ORDER_STATUSES.filter((s) => s !== order.status).map((s) => (
                      <Button key={s} size="sm" variant="ghost" onClick={() => handleStatusChange(order.id, s)} data-testid={`status-${order.id}-${s}`}
                        className="h-7 px-2 text-xs" style={{ color: ORDER_STATUS_STYLE[s].color, border: `1px solid ${ORDER_STATUS_STYLE[s].color}30` }}>
                        {ORDER_STATUS_STYLE[s].label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-16" style={{ color: "var(--text-tertiary)" }}>
            <Package size={32} className="mx-auto mb-4 opacity-30" />
            <p className="text-sm">{filter === "all" ? "No orders yet" : `No ${filter} orders`}</p>
          </div>
        )}
      </div>
    </>
  );
}

// ==================== Email Queue Tab ====================
export function EmailQueueTab({ emails, onRefetch }) {
  const axiosAdmin = axios.create({ withCredentials: true });
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [filter, setFilter] = useState("all");

  const handleMarkHandled = async (emailId) => {
    try {
      await axiosAdmin.patch(`${API}/admin/email-queue/${emailId}/handled`);
      toast.success("Marked as handled");
      onRefetch();
    } catch { toast.error("Failed to update"); }
  };

  const filtered = filter === "all" ? emails : emails.filter((e) => e.status === filter);
  const pendingCount = emails.filter((e) => e.status === "pending").length;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            {pendingCount > 0 && <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold mr-2" style={{ background: "var(--error)", color: "#fff", borderRadius: "50%" }}>{pendingCount}</span>}
            {emails.length} email{emails.length !== 1 ? "s" : ""} total
          </p>
          <div className="flex gap-1">
            {["all", "pending", "handled"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} className="px-3 py-1 text-xs uppercase tracking-wider font-semibold transition-colors" data-testid={`filter-email-${f}`}
                style={{ background: filter === f ? "var(--accent)" : "var(--bg-secondary)", color: filter === f ? "#fff" : "var(--text-secondary)", border: "1px solid var(--border)" }}>
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3" data-testid="email-queue-list">
        {filtered.map((email) => (
          <div key={email.id} style={{ background: "var(--bg-secondary)", border: `1px solid ${email.status === "pending" ? "rgba(239,68,68,0.3)" : "var(--border)"}` }}>
            <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpandedEmail(expandedEmail === email.id ? null : email.id)} data-testid={`email-row-${email.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {email.status === "pending"
                  ? <Clock size={16} style={{ color: "var(--error)" }} className="flex-shrink-0" />
                  : <CheckCircle2 size={16} style={{ color: "var(--success)" }} className="flex-shrink-0" />
                }
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{email.subject}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--text-tertiary)" }}>
                    To: <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{email.to_email}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>
                  {formatDate(email.created_at)}
                </span>
                {email.status === "pending" && (
                  <Button size="sm" onClick={(e) => { e.stopPropagation(); handleMarkHandled(email.id); }} data-testid={`handle-${email.id}`}
                    className="h-7 px-3 text-xs" style={{ background: "var(--success)", color: "#fff" }}>
                    <Check size={12} className="mr-1" /> Handled
                  </Button>
                )}
                {email.status === "handled" && (
                  <span className="text-xs px-2 py-0.5" style={{ background: "rgba(34,197,94,0.08)", color: "var(--success)", border: "1px solid rgba(34,197,94,0.2)" }}>Done</span>
                )}
                {expandedEmail === email.id ? <ChevronUp size={14} style={{ color: "var(--text-tertiary)" }} /> : <ChevronDown size={14} style={{ color: "var(--text-tertiary)" }} />}
              </div>
            </div>

            {expandedEmail === email.id && (
              <div className="px-4 pb-4" style={{ borderTop: "1px solid var(--border)" }}>
                <div className="mt-3 flex items-center gap-4 mb-3">
                  <a href={`mailto:${email.to_email}?subject=Re: ${encodeURIComponent(email.subject)}`}
                    className="text-xs font-semibold underline underline-offset-2" style={{ color: "var(--accent)" }} data-testid={`mailto-${email.id}`}>
                    Reply to {email.to_email} in Gmail
                  </a>
                  <button onClick={() => { navigator.clipboard.writeText(email.to_email); toast.success("Email copied"); }}
                    className="text-xs underline" style={{ color: "var(--text-tertiary)" }}>
                    Copy email
                  </button>
                </div>
                <div className="p-4 text-sm overflow-auto" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)", maxHeight: "400px" }}
                  dangerouslySetInnerHTML={{ __html: email.html_content }} data-testid={`email-content-${email.id}`} />
                {email.handled_by && (
                  <p className="text-xs mt-2" style={{ color: "var(--text-tertiary)" }}>
                    Handled by {email.handled_by} on {formatDate(email.handled_at)}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-16" style={{ color: "var(--text-tertiary)" }}>
            <Mail size={32} className="mx-auto mb-4 opacity-30" />
            <p className="text-sm mb-2">{filter === "all" ? "No emails yet" : `No ${filter} emails`}</p>
            <p className="text-xs">Emails from form submissions, payments, and follow-ups will appear here</p>
          </div>
        )}
      </div>
    </>
  );
}

// ==================== Widget Embed Tab ====================
export function WidgetEmbedTab({ embedCode }) {
  const [copied, setCopied] = useState(false);
  const copyEmbed = () => { navigator.clipboard.writeText(embedCode); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", padding: "32px" }}>
      <h2 className="text-lg mb-4" style={{ color: "var(--text-primary)", fontFamily: "'Playfair Display', serif" }}>Embed Chat Widget</h2>
      <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>Add this code to any website to show the Green Routes chat widget.</p>
      <div className="relative">
        <pre className="p-4 text-xs overflow-x-auto" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)", fontFamily: "'JetBrains Mono', monospace", color: "var(--text-primary)" }} data-testid="embed-code">
          {embedCode}
        </pre>
        <Button onClick={copyEmbed} data-testid="btn-copy-embed" className="absolute top-3 right-3 text-xs" variant="ghost" style={{ border: "1px solid var(--border)" }}>
          {copied ? <><Check size={12} className="mr-1" /> Copied</> : <><Copy size={12} className="mr-1" /> Copy</>}
        </Button>
      </div>
    </div>
  );
}


// ==================== Sold Out Tab ====================
const ALL_MENU_ITEMS = [
  /* Vapes */
  "2G Disposable","3G Disposable","3.5G Disposable","1G Screw-On Cart","Wholesale Box (100 pcs)",
  /* Moonrocks */
  "Moonrock Oz","Moonrock Full Box (16 Jars)",
  /* Standard Concentrates */
  "Standard 1G","Standard 3G","Standard 5G","Standard 7G","Standard 14G","Standard 1 OZ",
  /* Premium */
  "Diamond Sugar Wax 1G","Diamond Sugar Wax 3G","Diamond Sugar Wax 5G","Diamond Sugar Wax 7G","Diamond Sugar Wax 14G","Diamond Sugar Wax 1 OZ",
  "Solid Diamond 1G","Solid Diamond 3G","Solid Diamond 5G","Solid Diamond 7G","Solid Diamond 14G","Solid Diamond 1 OZ",
  "Live Rosin (Dank Rosin) 1G","Live Rosin (Dank Rosin) 3G","Live Rosin (Dank Rosin) 5G","Live Rosin (Dank Rosin) 7G","Live Rosin (Dank Rosin) 14G","Live Rosin (Dank Rosin) 1 OZ",
  /* Featured */
  "Dirty Sprite (7G Resin)","Pure Ice Cream (7G Badder)","Hash — Hubba Bubba",
  /* Hash/Rosin */
  "Hash 1000G","Hash 500G","LA Mousse (100G Bar)","Bubble Hash (100G Bar)","Static Hash (50G Bar)",
  /* Mushrooms */
  "Waffle Cones (4G)","Mushroom Chocolate Bar","Mushroom Gummies","Vision Mushroom Gummies",
  "Raw Mushroom Oz","Raw Mushroom Half P","Raw Mushroom Full Unit",
  /* Flower/Edibles */
  "Breakdown Pack Oz","Candy Edible","Chocolate Edible","Gummy Edible",
  /* Oz Specials */
  "Zour Smallz (Oz)","Frog Venom (Oz)",
];

export function SoldOutTab({ soldOutItems, onRefetch }) {
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(null);
  const [customItem, setCustomItem] = useState("");
  const axiosAdmin = axios.create({ withCredentials: true });

  const soldOutSet = new Set(soldOutItems.map((i) => i.name));
  const filtered = ALL_MENU_ITEMS.filter((n) => n.toLowerCase().includes(search.toLowerCase()));

  const toggleItem = async (name) => {
    setAdding(name);
    try {
      if (soldOutSet.has(name)) {
        await axiosAdmin.delete(`${API}/admin/sold-out/${encodeURIComponent(name)}`);
        toast.success(`${name} back in stock`);
      } else {
        await axiosAdmin.post(`${API}/admin/sold-out`, { name });
        toast.success(`${name} marked sold out`);
      }
      onRefetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setAdding(null); }
  };

  const addCustom = async () => {
    if (!customItem.trim()) return;
    setAdding(customItem);
    try {
      await axiosAdmin.post(`${API}/admin/sold-out`, { name: customItem.trim() });
      toast.success(`${customItem.trim()} marked sold out`);
      setCustomItem("");
      onRefetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setAdding(null); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold" style={{ color: "#F4F1ED" }}>Sold Out Management</h3>
        <span className="text-xs px-2.5 py-1" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>{soldOutItems.length} items sold out</span>
      </div>

      {soldOutItems.length > 0 && (
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>Currently Sold Out</p>
          <div className="flex flex-wrap gap-2">
            {soldOutItems.map((item) => (
              <button key={item.name} onClick={() => toggleItem(item.name)} disabled={adding === item.name}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-all hover:opacity-80"
                data-testid={`soldout-${item.name.replace(/\s/g, '-').toLowerCase()}`}
                style={{ background: "rgba(239,68,68,0.15)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.25)" }}>
                <Ban size={10} /> {item.name} <span style={{ opacity: 0.5 }}>&times;</span>
              </button>
            ))}
          </div>
          <p className="text-[10px] mt-2" style={{ color: "rgba(255,255,255,0.25)" }}>Click an item to mark it back in stock</p>
        </div>
      )}

      <div className="mb-4">
        <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>Add Custom Item</p>
        <div className="flex gap-2">
          <input type="text" value={customItem} onChange={(e) => setCustomItem(e.target.value)} placeholder="Type any item name..."
            onKeyDown={(e) => e.key === "Enter" && addCustom()}
            className="flex-1 px-3 py-2 text-xs outline-none" data-testid="soldout-custom-input"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#F4F1ED" }} />
          <Button size="sm" onClick={addCustom} disabled={!customItem.trim()} data-testid="soldout-custom-add"
            style={{ background: "#EF4444", color: "#fff", fontSize: "11px" }}>Mark Sold Out</Button>
        </div>
      </div>

      <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "rgba(255,255,255,0.4)" }}>Quick Toggle — Menu Items</p>
      <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..."
        className="w-full px-3 py-2 text-xs outline-none mb-3" data-testid="soldout-search"
        style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#F4F1ED" }} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-[400px] overflow-y-auto">
        {filtered.map((name) => {
          const isSoldOut = soldOutSet.has(name);
          return (
            <button key={name} onClick={() => toggleItem(name)} disabled={adding === name}
              className="flex items-center justify-between px-3 py-2 text-xs text-left transition-all"
              data-testid={`toggle-${name.replace(/\s/g, '-').toLowerCase()}`}
              style={{ background: isSoldOut ? "rgba(239,68,68,0.1)" : "rgba(255,255,255,0.03)", border: `1px solid ${isSoldOut ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.06)"}`, color: isSoldOut ? "#EF4444" : "rgba(255,255,255,0.6)" }}>
              <span style={{ textDecoration: isSoldOut ? "line-through" : "none" }}>{name}</span>
              <span className="text-[10px] font-bold uppercase" style={{ color: isSoldOut ? "#EF4444" : "rgba(16,185,129,0.6)" }}>{isSoldOut ? "Sold Out" : "In Stock"}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
